import { DomHelper } from '../modules/DomHelper';

/**
 * Drawer — SP ナビゲーション用ドロワー
 *
 * HTML 構造:
 *   <button class="js-drawer-trigger" aria-expanded="false" aria-controls="drawer">
 *   <div id="drawer" class="js-drawer" aria-hidden="true" inert>
 *     <nav>...</nav>
 *   </div>
 *   <div class="js-drawer-overlay"></div>
 */
export class Drawer {
  private readonly trigger: HTMLButtonElement;
  private readonly drawer: HTMLElement;
  private readonly overlay: HTMLElement | null;
  private isOpen = false;

  constructor(options?: {
    triggerSelector?: string;
    drawerSelector?: string;
    overlaySelector?: string;
  }) {
    const {
      triggerSelector = '.js-drawer-trigger',
      drawerSelector = '.js-drawer',
      overlaySelector = '.js-drawer-overlay',
    } = options ?? {};

    const trigger = DomHelper.qs<HTMLButtonElement>(triggerSelector);
    const drawer = DomHelper.qs<HTMLElement>(drawerSelector);

    if (!trigger || !drawer) {
      throw new Error('Drawer: trigger または drawer 要素が見つかりません');
    }

    this.trigger = trigger;
    this.drawer = drawer;
    this.overlay = DomHelper.qs<HTMLElement>(overlaySelector);

    this.init();
  }

  private init(): void {
    this.trigger.setAttribute('aria-expanded', 'false');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.setAttribute('inert', '');

    this.trigger.addEventListener('click', () => this.toggle());
    this.overlay?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });
  }

  open(): void {
    this.isOpen = true;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.trigger.setAttribute('aria-label', 'メニューを閉じる');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.drawer.removeAttribute('inert');
    DomHelper.addClass(this.drawer, 'is-open');
    DomHelper.addClass(document.body, 'is-drawer-open');
    DomHelper.lockScroll();

    // GTM イベント
    DomHelper.pushDataLayer({ event: 'drawer_open' });
  }

  close(): void {
    this.isOpen = false;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('aria-label', 'メニューを開く');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.setAttribute('inert', '');
    DomHelper.removeClass(this.drawer, 'is-open');
    DomHelper.removeClass(document.body, 'is-drawer-open');
    DomHelper.unlockScroll();
  }

  toggle(): void {
    this.isOpen ? this.close() : this.open();
  }
}
